// src/services/pushNotifications.ts
/**
 * Web Push notification helpers.
 *
 * subscribeToPush()   — request permission → subscribe → save to backend
 * unsubscribeFromPush() — unsubscribe in browser + remove from backend
 *
 * Device management:
 *   listPushDevices / renamePushDevice / deletePushDevice / sendTestPush
 */

import api from '@services/api'

const LS_KEY = 'immotron-push-subscribed'

// ---------------------------------------------------------------------------
// Device types (mirror backend PushDeviceItem / DeviceListResponse)
// ---------------------------------------------------------------------------

export interface PushDevice {
  id: number
  device_name: string | null
  user_agent: string | null
  endpoint_preview: string
  lang: string
  created_at: string | null
  updated_at: string | null
}

export interface PushDeviceList {
  vapid_configured: boolean
  devices: PushDevice[]
}

export interface TestPushResult {
  ok: boolean
  sent_to: number
  message: string
}

// ---------------------------------------------------------------------------
// Push state (synced with actual browser state)
// ---------------------------------------------------------------------------

export type PushState = 'unsupported' | 'default' | 'denied' | 'active' | 'inactive'

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function isPushPermissionGranted(): boolean {
  return isPushSupported() && Notification.permission === 'granted'
}

export function isPushPermissionDenied(): boolean {
  return isPushSupported() && Notification.permission === 'denied'
}

export function isPushSubscribed(): boolean {
  return localStorage.getItem(LS_KEY) === '1'
}

/**
 * Check the real browser push state (async — queries PushManager).
 * This is the source of truth, not localStorage.
 */
export async function getActualPushState(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported'

  if (Notification.permission === 'denied') return 'denied'
  if (Notification.permission === 'default') return 'default'

  // Permission is 'granted' — check if there's an active subscription
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      localStorage.setItem(LS_KEY, '1')
      return 'active'
    }
    localStorage.removeItem(LS_KEY)
    return 'inactive'
  } catch {
    return 'inactive'
  }
}

// ---------------------------------------------------------------------------
// Convert VAPID key
// ---------------------------------------------------------------------------

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

// ---------------------------------------------------------------------------
// Subscribe
// ---------------------------------------------------------------------------

export async function subscribeToPush(deviceName?: string): Promise<boolean> {
  if (!isPushSupported()) return false

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return false

    // Get VAPID public key from backend
    const { data } = await api.get<{ public_key: string }>('/api/notifications/push/vapid-key')
    if (!data.public_key) return false

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key).buffer as ArrayBuffer,
    })

    const subJson = subscription.toJSON()
    const lang = document.documentElement.lang || localStorage.getItem('i18nextLng') || 'de'

    await api.post('/api/notifications/push/subscribe', {
      endpoint: subJson.endpoint,
      p256dh: subJson.keys?.p256dh,
      auth: subJson.keys?.auth,
      lang: lang.substring(0, 2),
      ...(deviceName ? { device_name: deviceName } : {}),
    })

    localStorage.setItem(LS_KEY, '1')
    return true
  } catch (err) {
    console.warn('Push subscribe failed:', err)
    return false
  }
}

// ---------------------------------------------------------------------------
// Unsubscribe
// ---------------------------------------------------------------------------

export async function unsubscribeFromPush(): Promise<void> {
  localStorage.removeItem(LS_KEY)

  if (!isPushSupported()) return

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    const endpoint = subscription.endpoint

    // Unsubscribe in browser
    await subscription.unsubscribe()

    // Remove from backend
    await api.post('/api/notifications/push/unsubscribe', { endpoint })
  } catch (err) {
    console.warn('Push unsubscribe failed:', err)
  }
}

// ---------------------------------------------------------------------------
// Device management
// ---------------------------------------------------------------------------

export async function listPushDevices(): Promise<PushDeviceList> {
  const { data } = await api.get<PushDeviceList>('/api/notifications/push/devices')
  return data
}

export async function renamePushDevice(id: number, deviceName: string | null): Promise<PushDevice> {
  const { data } = await api.patch<PushDevice>(
    `/api/notifications/push/devices/${id}`,
    { device_name: deviceName },
  )
  return data
}

export async function deletePushDevice(id: number): Promise<void> {
  await api.delete(`/api/notifications/push/devices/${id}`)
}

export async function sendTestPush(): Promise<TestPushResult> {
  const { data } = await api.post<TestPushResult>('/api/notifications/push/test')
  return data
}
